import { useMemo } from 'react';

import { mapRateSourceToDTO } from '@/modules/common/mappers/rate-source.mapper';
import { RateSource } from '@/modules/common/types/rate-source';
import { FieldSyncRule } from '@/modules/projects/components/modals/resolve-conflicts-modal';
import { ProjectBillingModel } from '@/modules/projects/types/project';
import { ProjectTeamMember } from '@/modules/projects/types/project-team-member';
import { getMemberRateBySource } from '@/modules/projects/utils';

import { ProjectBillingParentFieldKey } from './project-billing-conflict-config';

const getMemberRateForRateSource = (member: ProjectTeamMember, rateSource: RateSource): number => {
  if (rateSource === RateSource.FlatRate) {
    return 0;
  }

  return getMemberRateBySource({
    ...member,
    activeRateSource: rateSource,
  });
};

const setAllMembersToRateSource = ({
  teamMembers,
  rateSource,
  setFieldValueIfChanged,
}: {
  teamMembers: ProjectTeamMember[];
  rateSource: RateSource;
  setFieldValueIfChanged: (field: string, value: unknown) => void;
}) => {
  teamMembers.forEach(member => {
    const fieldKey = `${ProjectBillingParentFieldKey.UserRates}_${member.id}`;

    setFieldValueIfChanged(fieldKey, {
      rateSource: mapRateSourceToDTO(rateSource),
      rate: getMemberRateForRateSource(member, rateSource),
    });
  });
};

/**
 * Returns field sync rules for the project billing conflict resolution modal.
 *
 * Rules:
 * 1. billingModel → when NonBillable, force rateSource to FlatRate and flatRate to 0.
 *    When restored from NonBillable, reset to the original form values.
 * 2. rateSource → propagate the selected rate source to all team member sub-rows,
 *    or restore individual member values when switching back to ProjectRateCard.
 *
 * @param getOriginalFormValues - Returns the billing form values at the time the modal opens.
 * @param teamMembers           - The current list of team members (used for sub-row updates).
 */
export const useProjectBillingSyncRules = (
  getOriginalFormValues: () => Record<string, unknown>,
  teamMembers: ProjectTeamMember[]
): FieldSyncRule[] => {
  return useMemo(
    (): FieldSyncRule[] => [
      {
        watchField: 'billingModel',
        onValueChange: (billingModel, _setFieldValue, _getFieldValue, setFieldValueIfChanged) => {
          if (!billingModel) return;

          const originalValues = getOriginalFormValues();

          if (billingModel === ProjectBillingModel.NonBillable) {
            setFieldValueIfChanged('rateSource', RateSource.FlatRate);
            setFieldValueIfChanged('flatRate', 0);
            setAllMembersToRateSource({
              teamMembers,
              rateSource: RateSource.FlatRate,
              setFieldValueIfChanged,
            });
          } else if (billingModel === ProjectBillingModel.FixedPrice) {
            // Fixed Price has no rate source, flat rate, or user rates
            setFieldValueIfChanged('rateSource', null);
            setFieldValueIfChanged('flatRate', null);
          } else {
            setFieldValueIfChanged('rateSource', originalValues['rateSource'] ?? null);
            setFieldValueIfChanged('flatRate', originalValues['flatRate'] ?? null);
          }
        },
      },
      {
        watchField: 'rateSource',
        onValueChange: (rateSource, _setFieldValue, _getFieldValue, setFieldValueIfChanged) => {
          if (!rateSource) return;

          if (rateSource === RateSource.ProjectRateCard) {
            // Restore each member's individual rate source from the original form values
            teamMembers.forEach((member: ProjectTeamMember) => {
              const fieldKey = `${ProjectBillingParentFieldKey.UserRates}_${member.id}`;

              setFieldValueIfChanged(fieldKey, {
                rateSource: mapRateSourceToDTO(member.activeRateSource),
                rate: getMemberRateBySource(member),
              });
            });
          } else {
            // Propagate the single selected rate source to all team member rows
            setAllMembersToRateSource({
              teamMembers,
              rateSource: rateSource as RateSource,
              setFieldValueIfChanged,
            });
          }
        },
      },
    ],
    [getOriginalFormValues, teamMembers]
  );
};

/**
 * Filters resolved billing values before sending to the API.
 * - For Fixed Price billing model: removes rateSource, flatRate, and userRates entirely.
 *
 * Note: userRates entries with null value (user selected "-") are intentionally preserved here
 * so that handleResolveConflicts / applyResolvedUserRates can detect which members to exclude
 * from selectedTeamMembers. Those entries are removed from selectedTeamMembers before the
 * billing mapper runs, so they never reach the API payload.
 */
export const filterBillingResolvedValues = (
  resolvedValues: Record<string, unknown>
): Record<string, unknown> => {
  const userRatesKey = ProjectBillingParentFieldKey.UserRates as string;

  if (resolvedValues['billingModel'] === ProjectBillingModel.FixedPrice) {
    const filtered = { ...resolvedValues };
    delete filtered['rateSource'];
    delete filtered['flatRate'];
    delete filtered[userRatesKey];
    return filtered;
  }

  return resolvedValues;
};
